import { HttpErrorResponse, HttpEvent, HttpInterceptorFn, HttpRequest, HttpHandlerFn } from '@angular/common/http';
import { inject } from '@angular/core';
import { Router } from '@angular/router';
import { Observable, catchError, finalize, map, shareReplay, switchMap, throwError } from 'rxjs';
import { AuthService } from './auth.service';
import { NotificationService } from '../notifications/notification.service';

// Refresh en vuelo compartido: todas las requests con 401 se suscriben al
// MISMO observable, así éxito Y error se propagan a todas (sin cuelgues).
/** Refresh en vuelo compartido por todas las peticiones con 401; `null` si no hay ninguno. */
let refresh$: Observable<string> | null = null;
// Evita avisos/navegaciones duplicados cuando varias requests fallan a la vez.
/** Instante (ms desde epoch) del último logout forzado, para avisar y navegar una sola vez. */
let lastForcedLogout = 0;

/**
 * Interceptor HTTP global de autenticación.
 *
 * Adjunta `Authorization: Bearer` a toda petición salvo login, refresh, logout y health (que no
 * deben llevarlo o no lo necesitan). Ante un 403 `PRIMER_LOGIN_REQUERIDO` manda a la pantalla de
 * primer login; ante un 401 renueva la sesión y reintenta la petición original de forma transparente.
 * @param req - Petición saliente.
 * @param next - Siguiente manejador de la cadena.
 * @returns Observable con los eventos de la respuesta (de la petición original o de su reintento);
 *   el 403 de primer login pendiente (tras navegar) y los demás errores no 401 se propagan sin cambios.
 */
export const authInterceptor: HttpInterceptorFn = (req, next) => {
    const authService = inject(AuthService);
    const router = inject(Router);
    const notifications = inject(NotificationService);

    const skipAuth =
        req.url.includes('/auth/login') ||
        req.url.includes('/auth/refresh') ||
        req.url.includes('/auth/logout') ||
        req.url.includes('/health');

    const token = authService.getToken();
    let authReq = req;
    if (token && !skipAuth) {
        authReq = req.clone({
            setHeaders: { Authorization: `Bearer ${token}` },
        });
    }

    return next(authReq).pipe(
        catchError((error: HttpErrorResponse) => {
            // Usuario con primer login pendiente: el backend bloquea el negocio.
            // El código viaja en `message` (detail se anula en producción).
            if (error.status === 403 && error.error?.message === 'PRIMER_LOGIN_REQUERIDO') {
                router.navigate(['/primer-login']);
                return throwError(() => error);
            }
            if (error.status === 401 && !skipAuth) {
                return handleTokenRefresh(authService, router, notifications, authReq, next);
            }
            return throwError(() => error);
        })
    );
};

/**
 * Renueva el access token tras un 401 y reintenta la petición con el token nuevo.
 *
 * Sin refresh token o con el refresh caducado cierra la sesión. Si el backend está caído, o el
 * refresh falla por red o por un 5xx, espera la reconexión y vuelve a intentarlo sin borrar los
 * tokens: una caída del servidor no debe expulsar al usuario. Cualquier otro error (401/403 u otro
 * 4xx) fuerza el logout. Ojo: el `catchError` va después del `switchMap`, así que también atrapa los
 * errores de la petición reintentada, no solo los del refresh.
 * @param authService - Servicio de autenticación (tokens, refresh y estado del backend).
 * @param router - Router, para mandar al login al forzar el logout.
 * @param notifications - Toasts, para avisar que la sesión se cerró.
 * @param req - Petición original que recibió el 401.
 * @param next - Siguiente manejador, para reenviar la petición con el token nuevo.
 * @returns Observable con los eventos de la petición reintentada; falla con un `HttpErrorResponse` 401
 *   si no hay refresh token válido, o con el error que cerró la sesión (del refresh o del reintento).
 */
function handleTokenRefresh(
    authService: AuthService,
    router: Router,
    notifications: NotificationService,
    req: HttpRequest<unknown>,
    next: HttpHandlerFn,
): Observable<HttpEvent<unknown>> {
    if (!authService.getRefreshToken()) {
        forceLogout(authService, router, notifications);
        return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    if (authService.backendOffline()) {
        return authService.waitForOnline().pipe(
            switchMap(() => handleTokenRefresh(authService, router, notifications, req, next))
        );
    }

    if (authService.isRefreshTokenExpired()) {
        forceLogout(authService, router, notifications);
        return throwError(() => new HttpErrorResponse({ status: 401 }));
    }

    return sharedRefresh(authService).pipe(
        switchMap((newToken) => {
            const retryReq = req.clone({
                setHeaders: { Authorization: `Bearer ${newToken}` },
            });
            return next(retryReq);
        }),
        catchError((refreshError: HttpErrorResponse) => {
            // Transitorio (backend caído/reiniciando, red): esperar reconexión
            // y reintentar — NO borrar tokens.
            if (authService.isNetworkError(refreshError) || refreshError.status >= 500) {
                return authService.waitForOnline().pipe(
                    switchMap(() => handleTokenRefresh(authService, router, notifications, req, next))
                );
            }
            // Fallo definitivo de auth (401/403 del refresh): cerrar sesión.
            forceLogout(authService, router, notifications);
            return throwError(() => refreshError);
        })
    );
}

/**
 * Devuelve el refresh en vuelo o inicia uno nuevo, para que N peticiones con 401 simultáneas hagan un solo refresh.
 *
 * El refresh token rota en cada uso, así que dos refresh paralelos invalidarían la sesión; con
 * `shareReplay` todas reciben el mismo access token (o el mismo error) y `finalize` libera el
 * observable compartido al terminar.
 * @param authService - Servicio que ejecuta el refresh contra el backend.
 * @returns Observable que emite el nuevo access token.
 */
function sharedRefresh(authService: AuthService): Observable<string> {
    if (!refresh$) {
        refresh$ = authService.refreshToken().pipe(
            map((res) => res.access_token),
            finalize(() => {
                // Limpia también en error: el siguiente 401 inicia un refresh nuevo.
                refresh$ = null;
            }),
            shareReplay({ bufferSize: 1, refCount: false })
        );
    }
    return refresh$;
}

/**
 * Cierra la sesión, avisa al usuario y lo manda al login, como máximo una vez cada 3 segundos.
 * @param authService - Servicio que borra los tokens.
 * @param router - Router, para navegar al login.
 * @param notifications - Toasts, para el aviso de sesión cerrada.
 */
function forceLogout(authService: AuthService, router: Router, notifications: NotificationService): void {
    // Dedupe: varias requests pueden fallar a la vez; avisamos y navegamos 1 vez.
    const now = Date.now();
    if (now - lastForcedLogout < 3000) return;
    lastForcedLogout = now;

    authService.logout();
    notifications.warning('Tu sesión fue cerrada. Inicia sesión nuevamente.');
    router.navigate(['/login']);
}
